import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  ArrowDownTrayIcon,
  CalendarDaysIcon,
  ClockIcon,
  EnvelopeIcon,
  FilmIcon,
  MapPinIcon,
  TvIcon,
} from "@heroicons/react/24/outline";
import type { ColumnDef } from "@tanstack/react-table";
import Loader from "../../components/common/Loader";
import Table from "../../components/common/Table";
import EmailReportModal from "../../components/modals/EmailReportModal";
import CampaignThumb from "../../components/subComponents/CampaignThumb";
import { DailyDeliveryChart } from "../../components/subComponents/BrandReportCharts";
import { displayCreativeName } from "../../components/subComponents/campaignFormatting";
import {
  useBrandReport,
  useBrandReportCreatives,
  useBrandReportScreens,
} from "../../hooks/useBrandReport";
import useDebounce from "../../hooks/useDebounce";
import { isBrandDemoData } from "../../mocks/brandReportDemo";
import { brandReportService } from "../../api/services/brandReportService";
import { formatDisplayDateTime, toDisplayDateInput } from "../../utils/dateUtils";
import {
  REPORT_PRESETS,
  ReportPreset,
  formatAirtime,
  formatDayLong,
  groupByCity,
  hourLabel,
  playsByHour,
  presetRange,
  saveReportFile,
  validateRange,
} from "../../utils/brandReport";
import type {
  BrandReport,
  BrandReportFilter,
  BrandReportScreenRow,
  ReportFormat,
} from "../../schemas/brandReportSchema";

type ReportType = "campaigns" | "cities" | "screens" | "days" | "hours";

/** One report per question a brand asks — the tenant Reports page's pattern. */
const REPORT_TYPES: Array<{
  id: ReportType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}> = [
  { id: "campaigns", label: "Campaign-wise", icon: FilmIcon, description: "How much each campaign played." },
  { id: "cities", label: "City-wise", icon: MapPinIcon, description: "Where your campaigns played. Pick a city to see its areas." },
  { id: "screens", label: "Screen-wise", icon: TvIcon, description: "Proof of play for every screen." },
  { id: "days", label: "Day-wise", icon: CalendarDaysIcon, description: "How much played on each day." },
  { id: "hours", label: "Time of day", icon: ClockIcon, description: "What time of day your campaigns played (IST)." },
];

const SELECT =
  "rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 disabled:bg-gray-50 disabled:text-gray-400";
const th = "px-4 py-2 text-left font-medium";
const thNum = `${th} text-right`;
const td = "px-4 py-3 text-gray-800";
const tdNum = `${td} text-right tabular-nums`;

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString("en-IN")} ${n === 1 ? one : many}`;
const count = (n: number) => n.toLocaleString("en-IN");

/** A table in the popup's style: rounded, bordered, sticky grey header. */
const ReportTable: React.FC<{ head: React.ReactNode; children: React.ReactNode }> = ({
  head,
  children,
}) => (
  <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
    <table className="w-full min-w-[48rem] text-left text-sm">
      <thead className="sticky top-0 z-10 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
        <tr>{head}</tr>
      </thead>
      <tbody className="divide-y divide-gray-100">{children}</tbody>
    </table>
  </div>
);

/** Share of all plays, as a bar and a percentage. */
const Share: React.FC<{ value: number; total: number }> = ({ value, total }) => {
  const share = total ? value / total : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 flex-1 rounded bg-gray-100">
        <div
          className="h-2 rounded bg-primary-default"
          style={{ width: `${Math.max(share * 100, value ? 2 : 0)}%` }}
        />
      </div>
      <span className="w-10 text-right text-xs tabular-nums text-gray-500">
        {Math.round(share * 100)}%
      </span>
    </div>
  );
};

const lastPlayed = (iso: string | null) => (iso ? formatDisplayDateTime(iso) : "–");

const CampaignReport: React.FC<{ report: BrandReport }> = ({ report }) => (
  <ReportTable
    head={
      <>
        <th className={th}>Campaign</th>
        <th className={thNum}>Plays</th>
        <th className={`${th} w-48`}>Share of plays</th>
        <th className={thNum}>On screen</th>
        <th className={thNum}>Screens</th>
        <th className={th}>Last played</th>
      </>
    }
  >
    {report.byCreative.map((row) => (
      <tr key={row.creativeId}>
        <td className={td}>
          <div className="flex items-center gap-3">
            <CampaignThumb thumbnailUrl={row.thumbnailUrl} className="h-10 w-16 flex-shrink-0" />
            <span title={row.name} className="font-medium text-gray-900">
              {displayCreativeName(row.name)}
            </span>
          </div>
        </td>
        <td className={`${tdNum} font-semibold text-gray-900`}>{count(row.plays)}</td>
        <td className={td}>
          <Share value={row.plays} total={report.summary.plays} />
        </td>
        <td className={tdNum}>{formatAirtime(row.airtime)}</td>
        <td className={tdNum}>{count(row.screens)}</td>
        <td className={`${td} whitespace-nowrap`}>{lastPlayed(row.lastPlayedAt)}</td>
      </tr>
    ))}
  </ReportTable>
);

/**
 * Cities, or the areas of the chosen city. A row is a shortcut to the filter
 * above it, the way the map's list drills down.
 */
const CityReport: React.FC<{
  report: BrandReport;
  cityChosen: boolean;
  onCity: (cityId: string) => void;
  onArea: (regionId: string) => void;
}> = ({ report, cityChosen, onCity, onArea }) => {
  const total = report.summary.plays;
  const cities = useMemo(() => groupByCity(report.byLocation), [report]);
  const areas = useMemo(
    () => [...report.byLocation].sort((a, b) => b.plays - a.plays),
    [report],
  );
  const rowButton = "text-left font-medium text-primary-default hover:underline";

  return (
    <ReportTable
      head={
        <>
          <th className={th}>{cityChosen ? "Area" : "City"}</th>
          {!cityChosen && <th className={thNum}>Areas</th>}
          <th className={thNum}>Restaurants</th>
          <th className={thNum}>Screens</th>
          <th className={thNum}>Plays</th>
          <th className={`${th} w-48`}>Share of plays</th>
          <th className={thNum}>On screen</th>
        </>
      }
    >
      {cityChosen
        ? areas.map((row) => (
            <tr key={row.regionId ?? row.region ?? ""}>
              <td className={td}>
                {row.regionId ? (
                  <button type="button" className={rowButton} onClick={() => onArea(row.regionId!)}>
                    {row.region || "Unassigned"}
                  </button>
                ) : (
                  row.region || "Unassigned"
                )}
              </td>
              <td className={tdNum}>{count(row.restaurants)}</td>
              <td className={tdNum}>{count(row.screens)}</td>
              <td className={`${tdNum} font-semibold text-gray-900`}>{count(row.plays)}</td>
              <td className={td}>
                <Share value={row.plays} total={total} />
              </td>
              <td className={tdNum}>{formatAirtime(row.airtime)}</td>
            </tr>
          ))
        : cities.map((row) => (
            <tr key={row.cityId ?? row.city}>
              <td className={td}>
                {row.cityId ? (
                  <button type="button" className={rowButton} onClick={() => onCity(row.cityId!)}>
                    {row.city}
                  </button>
                ) : (
                  row.city
                )}
              </td>
              <td className={tdNum}>{count(row.areas)}</td>
              <td className={tdNum}>{count(row.restaurants)}</td>
              <td className={tdNum}>{count(row.screens)}</td>
              <td className={`${tdNum} font-semibold text-gray-900`}>{count(row.plays)}</td>
              <td className={td}>
                <Share value={row.plays} total={total} />
              </td>
              <td className={tdNum}>{formatAirtime(row.airtime)}</td>
            </tr>
          ))}
    </ReportTable>
  );
};

/**
 * Proof of play per screen, on the shared Table so it scrolls like the tenant
 * report screens: the next page loads as the list nears its end. Rows are
 * server-ordered by plays across all pages, so columns are not sortable.
 */
const ScreenReport: React.FC<{ filter: BrandReportFilter }> = ({ filter }) => {
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 350);
  const { screens, total, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useBrandReportScreens(filter, debounced.trim());

  const columns = useMemo<ColumnDef<BrandReportScreenRow>[]>(
    () => [
      {
        accessorKey: "screenName",
        header: "Screen",
        enableSorting: false,
        cell: ({ row }) => (
          <div>
            <p className="font-medium text-gray-900">{row.original.screenName}</p>
            {row.original.restaurantName &&
              row.original.restaurantName !== row.original.screenName && (
                <p className="text-xs text-gray-500">{row.original.restaurantName}</p>
              )}
          </div>
        ),
      },
      {
        id: "location",
        header: "Location",
        enableSorting: false,
        cell: ({ row }) =>
          [row.original.region, row.original.city].filter(Boolean).join(", ") || "–",
      },
      {
        accessorKey: "plays",
        header: "Plays",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-semibold text-gray-900">{count(row.original.plays)}</span>
        ),
      },
      {
        accessorKey: "airtime",
        header: "On screen",
        enableSorting: false,
        cell: ({ row }) => formatAirtime(row.original.airtime),
      },
      {
        accessorKey: "creatives",
        header: "Campaigns",
        enableSorting: false,
        cell: ({ row }) => count(row.original.creatives),
      },
      {
        accessorKey: "lastPlayedAt",
        header: "Last played",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="whitespace-nowrap">{lastPlayed(row.original.lastPlayedAt)}</span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-gray-600">
        {isLoading ? " " : `${plural(total, "screen")}${debounced.trim() ? " match your search" : ""}`}
      </p>
      <Table
        data={screens}
        columns={columns}
        searchable
        onSearch={setSearch}
        pagination={false}
        isLoading={isLoading}
        maxHeight="65vh"
        infinite
        hasNextPage={hasNextPage}
        isFetchingMore={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
      />
    </div>
  );
};

const DayReport: React.FC<{ report: BrandReport }> = ({ report }) => {
  // Newest first: the day a brand checks is usually yesterday or today.
  const days = useMemo(() => [...report.daily].reverse(), [report]);
  return (
    <div className="flex flex-col gap-3">
      <DailyDeliveryChart days={report.daily} />
      <ReportTable
        head={
          <>
            <th className={th}>Day</th>
            <th className={thNum}>Plays</th>
            <th className={thNum}>On screen</th>
            <th className={thNum}>Screens</th>
          </>
        }
      >
        {days.map((day) => (
          <tr key={day.date} className={day.plays ? "" : "text-gray-400"}>
            <td className={td}>{formatDayLong(day.date)}</td>
            <td className={`${tdNum} font-semibold text-gray-900`}>{count(day.plays)}</td>
            <td className={tdNum}>{formatAirtime(day.airtime)}</td>
            <td className={tdNum}>{count(day.screens)}</td>
          </tr>
        ))}
      </ReportTable>
    </div>
  );
};

const HourReport: React.FC<{ report: BrandReport }> = ({ report }) => {
  const hours = useMemo(() => playsByHour(report.hourly), [report]);
  const busiest = hours.indexOf(Math.max(...hours));
  return (
    <div className="flex flex-col gap-3">
      {hours[busiest] > 0 && (
        <p className="text-sm text-gray-600">
          Your campaigns played most between{" "}
          <b className="text-gray-900">{hourLabel(busiest)}</b>.
        </p>
      )}
      <ReportTable
        head={
          <>
            <th className={th}>Time (IST)</th>
            <th className={thNum}>Plays</th>
            <th className={`${th} w-1/2`}>Share of plays</th>
          </>
        }
      >
        {hours.map((plays, hour) => (
          <tr key={hour} className={plays ? "" : "text-gray-400"}>
            <td className={`${td} whitespace-nowrap`}>{hourLabel(hour)}</td>
            <td className={`${tdNum} font-semibold text-gray-900`}>{count(plays)}</td>
            <td className={td}>
              <Share value={plays} total={report.summary.plays} />
            </td>
          </tr>
        ))}
      </ReportTable>
    </div>
  );
};

/**
 * The brand's reports: pick a report type, narrow it with the filters, read the
 * table. Every report shares the same filters, so switching type keeps them.
 *
 * Figures are the raw play logs on the server, so they are exactly what the
 * screens reported — a tenant's edited creative report is never reflected
 * here. Downloads and emails are built server-side from the same filters, so
 * the file always matches the page.
 */
const BrandReports: React.FC = () => {
  const [reportType, setReportType] = useState<ReportType>("campaigns");
  const [preset, setPreset] = useState<ReportPreset>("30d");
  const initial = presetRange("30d");
  const [customStart, setCustomStart] = useState(initial.startDate);
  const [customEnd, setCustomEnd] = useState(initial.endDate);
  const [creativeId, setCreativeId] = useState("");
  const [cityId, setCityId] = useState("");
  const [regionId, setRegionId] = useState("");

  const range =
    preset === "custom" ? { startDate: customStart, endDate: customEnd } : presetRange(preset);
  const rangeError = preset === "custom" ? validateRange(customStart, customEnd) : null;

  // The city and area lists come from the same period and campaign with no
  // place filter. With no place chosen that is the very report on screen, so
  // react-query serves it from cache rather than asking twice.
  const optionsFilter = useMemo<BrandReportFilter>(
    () => ({
      startDate: range.startDate,
      endDate: range.endDate,
      creativeIds: creativeId ? [creativeId] : [],
    }),
    [range.startDate, range.endDate, creativeId],
  );
  const { report: optionsReport } = useBrandReport(optionsFilter);
  const cities = useMemo(
    () => groupByCity(optionsReport?.byLocation ?? []).filter((c) => c.cityId),
    [optionsReport],
  );
  const areas = useMemo(
    () =>
      (optionsReport?.byLocation ?? [])
        .filter((row) => row.cityId === cityId && row.regionId)
        .sort((a, b) => (a.region ?? "").localeCompare(b.region ?? "")),
    [optionsReport, cityId],
  );
  // A place the other filters no longer reach falls back to "all" — the same
  // rule as the campaigns popup. Kept while the lists are still loading.
  const activeCityId = !optionsReport || cities.some((c) => c.cityId === cityId) ? cityId : "";
  const activeRegionId =
    activeCityId && (!optionsReport || areas.some((a) => a.regionId === regionId)) ? regionId : "";

  const filter = useMemo<BrandReportFilter>(
    () => ({
      ...optionsFilter,
      cityId: activeCityId || undefined,
      regionId: activeRegionId || undefined,
    }),
    [optionsFilter, activeCityId, activeRegionId],
  );

  const { creatives, isLoading: creativesLoading } = useBrandReportCreatives();
  const { report, isLoading, isFetching, isError, refetch } = useBrandReport(filter);

  const [downloading, setDownloading] = useState<ReportFormat | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const handleDownload = async (format: ReportFormat) => {
    setDownloading(format);
    try {
      saveReportFile(await brandReportService.download(filter, format));
    } catch (error) {
      console.error("Failed to download brand report:", error);
      toast.error("Could not generate the report. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  const handleEmail = async (email: string, format: ReportFormat) => {
    setSending(true);
    try {
      await brandReportService.sendEmail(filter, email, format);
      toast.success(`Report sent to ${email}`);
      setEmailOpen(false);
    } catch (error) {
      console.error("Failed to email brand report:", error);
      toast.error("Could not send the report. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const chooseCity = (id: string) => {
    setCityId(id);
    setRegionId("");
  };

  const today = toDisplayDateInput();
  const summary = report?.summary;
  const hasPlays = (summary?.plays ?? 0) > 0;
  const canExport = !rangeError && !!report;
  const activeType = REPORT_TYPES.find((t) => t.id === reportType)!;
  const exportButton =
    "flex items-center gap-1.5 rounded border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60";

  return (
    <div className="flex flex-col gap-4 px-6 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-800">
            Reports
            {isBrandDemoData() && (
              <span className="rounded bg-warning-light px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800">
                Demo data
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-500">
            Choose a report, then filter it by period, campaign or place.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => handleDownload("PDF")}
            disabled={!canExport || downloading !== null}
            className={exportButton}
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {downloading === "PDF" ? "Preparing…" : "PDF"}
          </button>
          <button
            type="button"
            onClick={() => handleDownload("EXCEL")}
            disabled={!canExport || downloading !== null}
            className={exportButton}
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {downloading === "EXCEL" ? "Preparing…" : "Excel"}
          </button>
          <button
            type="button"
            onClick={() => setEmailOpen(true)}
            disabled={!canExport}
            className={exportButton}
          >
            <EnvelopeIcon className="h-4 w-4" />
            Email
          </button>
        </div>
      </div>

      <div role="tablist" aria-label="Report type" className="flex flex-wrap gap-2">
        {REPORT_TYPES.map(({ id, label, icon: Icon }) => {
          const on = id === reportType;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setReportType(id)}
              className={`flex items-center gap-2 rounded border px-4 py-2 text-sm font-medium transition ${
                on
                  ? "border-primary-default bg-primary-default text-white"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Period"
            className={SELECT}
            value={preset}
            onChange={(event) => {
              const next = event.target.value as ReportPreset;
              if (next === "custom" && preset !== "custom") {
                // Start the custom range from whatever was showing.
                setCustomStart(range.startDate);
                setCustomEnd(range.endDate);
              }
              setPreset(next);
            }}
          >
            {REPORT_PRESETS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {preset === "custom" && (
            <>
              <input
                type="date"
                aria-label="Start date"
                value={customStart}
                max={today}
                onChange={(event) => setCustomStart(event.target.value)}
                className={SELECT}
              />
              <span className="text-sm text-gray-500">to</span>
              <input
                type="date"
                aria-label="End date"
                value={customEnd}
                max={today}
                onChange={(event) => setCustomEnd(event.target.value)}
                className={SELECT}
              />
            </>
          )}

          <span className="mx-1 hidden h-6 w-px bg-gray-200 sm:block" />

          <select
            aria-label="Campaign"
            className={`${SELECT} max-w-[16rem]`}
            value={creativeId}
            disabled={creativesLoading}
            onChange={(event) => setCreativeId(event.target.value)}
          >
            <option value="">All campaigns</option>
            {creatives.map((option) => (
              <option key={option.id} value={option.id}>
                {displayCreativeName(option.name)}
                {option.isDeleted ? " (removed)" : ""}
              </option>
            ))}
          </select>
          <select
            aria-label="City"
            className={SELECT}
            value={activeCityId}
            onChange={(event) => chooseCity(event.target.value)}
          >
            <option value="">All cities</option>
            {cities.map((c) => (
              <option key={c.cityId!} value={c.cityId!}>
                {c.city}
              </option>
            ))}
          </select>
          <select
            aria-label="Area"
            className={SELECT}
            value={activeRegionId}
            disabled={!activeCityId}
            onChange={(event) => setRegionId(event.target.value)}
          >
            <option value="">All areas</option>
            {areas.map((a) => (
              <option key={a.regionId!} value={a.regionId!}>
                {a.region}
              </option>
            ))}
          </select>

          {(creativeId || activeCityId) && (
            <button
              type="button"
              onClick={() => {
                setCreativeId("");
                chooseCity("");
              }}
              className="px-2 text-sm font-medium text-primary-default hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>

        {rangeError ? (
          <p className="text-sm text-red-600">{rangeError}</p>
        ) : (
          summary && (
            <p className="text-sm text-gray-600">
              <b className="text-gray-900">{count(summary.plays)}</b> plays ·{" "}
              <b className="text-gray-900">{formatAirtime(summary.airtime)}</b> on screen ·{" "}
              <b className="text-gray-900">{plural(summary.screens, "screen")}</b> in{" "}
              <b className="text-gray-900">{plural(summary.cities, "city", "cities")}</b> ·{" "}
              <b className="text-gray-900">{plural(summary.creatives, "campaign")}</b>
            </p>
          )
        )}
      </div>

      {rangeError ? null : isLoading ? (
        <div className="py-16">
          <Loader />
        </div>
      ) : isError || !report ? (
        <div className="rounded-lg border border-gray-200 bg-white py-16 text-center">
          <p className="text-sm text-gray-600">The report could not be loaded.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-2 text-sm font-medium text-primary-default hover:underline"
          >
            Try again
          </button>
        </div>
      ) : !hasPlays ? (
        <div className="rounded-lg border border-gray-200 bg-white py-16 text-center">
          <p className="font-medium text-gray-700">No plays in this period.</p>
          <p className="mt-1 text-sm text-gray-500">
            Try a longer period or fewer filters, or check that your campaigns are scheduled on the
            My Campaigns page.
          </p>
        </div>
      ) : (
        <section className={`flex flex-col gap-3 transition-opacity ${isFetching ? "opacity-60" : ""}`}>
          <div>
            <h2 className="text-lg font-semibold text-gray-800">{activeType.label} report</h2>
            <p className="text-sm text-gray-500">{activeType.description}</p>
          </div>
          {reportType === "campaigns" && <CampaignReport report={report} />}
          {reportType === "cities" && (
            <CityReport
              report={report}
              cityChosen={!!activeCityId}
              onCity={chooseCity}
              onArea={setRegionId}
            />
          )}
          {reportType === "screens" && <ScreenReport filter={filter} />}
          {reportType === "days" && <DayReport report={report} />}
          {reportType === "hours" && <HourReport report={report} />}
          <p className="text-xs text-gray-500">
            Figures come from the play logs each screen reports, in IST. Time on screen counts each
            play up to the campaign&apos;s own length.
          </p>
        </section>
      )}

      <EmailReportModal
        isOpen={emailOpen}
        onClose={() => setEmailOpen(false)}
        onSubmit={handleEmail}
        isLoading={sending}
        reportName="Campaign Report"
      />
    </div>
  );
};

export default BrandReports;
